import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import apiClient, { getApiErrorInfo } from '../../services/api';

interface AdminStats {
  totalTenants: number;
  totalUsers: number;
  newUsersLast30Days: number;
  totalCourses: number;
  totalRegistrations: number;
  completedRegistrations: number;
  activeSubscriptions: number;
  subscriptionsExpiringSoon: number;
}

interface WebhookSummary {
  last24Hours: number;
  succeeded: number;
  failed: number;
}

interface WebhookEvent {
  id: string;
  eventId: string;
  eventType: string;
  status: string;
  errorMessage?: string;
  processingDurationMs?: number;
  receivedAt: string;
  attemptCount: number;
}

interface WebhookData {
  summary: WebhookSummary;
  recent: WebhookEvent[];
}

interface StatCardProps {
  label: string;
  value: number | null;
  loading: boolean;
  highlight?: boolean;
}

function StatCard({ label, value, loading, highlight }: StatCardProps) {
  return (
    <Paper
      sx={{
        p: 3,
        border: 1,
        borderColor: highlight ? 'warning.main' : 'divider',
        borderWidth: highlight ? 2 : 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={500} sx={{ mt: 0.5 }}>
        {loading ? <CircularProgress size={28} /> : (value ?? '—')}
      </Typography>
    </Paper>
  );
}

function WebhookStatusChip({ status }: { status: string }) {
  const map: Record<string, { color: 'success' | 'error' | 'warning' | 'default'; label: string }> = {
    success: { color: 'success', label: 'Success' },
    failed: { color: 'error', label: 'Failed' },
    pending: { color: 'warning', label: 'Pending' },
    retry: { color: 'warning', label: 'Retry' },
  };
  const cfg = map[status] ?? { color: 'default', label: status };
  return <Chip label={cfg.label} color={cfg.color} size="small" />;
}

export default function SystemReports() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [webhooks, setWebhooks] = useState<WebhookData | null>(null);
  const [webhooksLoading, setWebhooksLoading] = useState(true);
  const [webhooksError, setWebhooksError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchStats() {
      try {
        const res = await apiClient.get('/api/admin/stats');
        setStats(res.data);
      } catch (err) {
        setError(getApiErrorInfo(err).message);
      } finally {
        setLoading(false);
      }
    }
    fetchStats();
  }, []);

  useEffect(() => {
    async function fetchWebhooks() {
      try {
        const res = await apiClient.get('/api/admin/webhooks?limit=50');
        setWebhooks(res.data);
      } catch (err) {
        setWebhooksError(getApiErrorInfo(err).message);
      } finally {
        setWebhooksLoading(false);
      }
    }
    fetchWebhooks();
  }, []);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Platform-wide statistics across all tenants and users.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
        Users &amp; Access
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 4 }}>
        <StatCard label="Total Tenants" value={stats?.totalTenants ?? null} loading={loading} />
        <StatCard label="Total Users" value={stats?.totalUsers ?? null} loading={loading} />
        <StatCard label="New Users (Last 30 Days)" value={stats?.newUsersLast30Days ?? null} loading={loading} />
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
        Content
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 4 }}>
        <StatCard label="Total Courses" value={stats?.totalCourses ?? null} loading={loading} />
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
        Engagement
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 4 }}>
        <StatCard label="Total Registrations" value={stats?.totalRegistrations ?? null} loading={loading} />
        <StatCard label="Completed Registrations" value={stats?.completedRegistrations ?? null} loading={loading} />
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
        Subscriptions
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2, mb: 4 }}>
        <StatCard label="Active Subscriptions" value={stats?.activeSubscriptions ?? null} loading={loading} />
        <StatCard
          label="Expiring Within 30 Days"
          value={stats?.subscriptionsExpiringSoon ?? null}
          loading={loading}
          highlight={(stats?.subscriptionsExpiringSoon ?? 0) > 0}
        />
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, mb: 1.5 }}>
        Webhooks (Last 24 Hours)
      </Typography>

      {webhooksError && <Alert severity="error" sx={{ mb: 3 }}>{webhooksError}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
        <StatCard label="Received" value={webhooks?.summary.last24Hours ?? null} loading={webhooksLoading} />
        <StatCard label="Succeeded" value={webhooks?.summary.succeeded ?? null} loading={webhooksLoading} />
        <StatCard
          label="Failed"
          value={webhooks?.summary.failed ?? null}
          loading={webhooksLoading}
          highlight={(webhooks?.summary.failed ?? 0) > 0}
        />
      </Box>

      {webhooksLoading ? (
        <CircularProgress size={24} />
      ) : webhooks && webhooks.recent.length > 0 ? (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Event Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Received</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Attempts</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {webhooks.recent.map((w) => (
                <TableRow key={w.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '13px' }}>{w.eventType}</TableCell>
                  <TableCell><WebhookStatusChip status={w.status} /></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontSize: '13px' }}>
                    {new Date(w.receivedAt).toLocaleString()}
                  </TableCell>
                  <TableCell sx={{ fontSize: '13px' }}>
                    {w.processingDurationMs != null ? `${w.processingDurationMs} ms` : '—'}
                  </TableCell>
                  <TableCell sx={{ fontSize: '13px' }}>{w.attemptCount}</TableCell>
                  <TableCell sx={{ fontSize: '13px', color: 'error.main', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {w.errorMessage ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        !webhooksError && (
          <Typography variant="body2" color="text.secondary">No webhook events recorded yet.</Typography>
        )
      )}
    </Box>
  );
}
